import React, { useContext } from "react";
import { Controller } from "react-hook-form";
import FormContext from "../../context/formContext";
import "./index.scss";

/**
 * @desc Custom input. Customizable:
 *  - input props
 *  - label props
 *  - error props
 *  - ignore error
 *  - checkbox feature
 *  - controlled boolean
 *  - pulls control from Form context
 */
export const Input = ({
  inputProps = {},
  labelProps = {},
  errorProps = {},
  fieldName,
  ignoreError = false,
  controlled = true,
}) => {
  // CONSTANTS ----------------------------------------------------------------

  const { control = {} } = useContext(FormContext);
  const isCheckbox = inputProps.type === "checkbox";

  // METHODS ------------------------------------------------------------------

  /**
   * @desc Label.
   */
  const renderLabel = () => {
    if (!labelProps.children && !isCheckbox) return;
    return (
      <label
        className={`${isCheckbox ? "custom" : "form"}-control-label`}
        {...labelProps}
      />
    );
  };

  /**
   * @desc Error for controlled inputs.
   */
  const renderError = ({ invalid, error }) => {
    if (!invalid || ignoreError) return;
    const errorIconProps = {
      src: "https://funko.com/on/demandware.static/Sites-FunkoUS-Site/-/default/dwe062a1ec/images/svg/error.svg",
      className: "svg-symbol error error-icon",
    };
    return (
      <div className="invalid-feedback" {...errorProps}>
        <img alt={"error-icon"} {...errorIconProps} />
        <span role="alert">{error.message}</span>
      </div>
    );
  };

  // COMPONENT ----------------------------------------------------------------

  if (!controlled) {
    const inputUncontrolledProps = {
      ...inputProps,
      className: `${inputProps.className} ${
        isCheckbox ? "custom-control-input" : ""
      }`,
    };
    return (
      <div
        className={`form-group ${inputProps.required ? "required" : ""} ${
          isCheckbox ? "custom-control custom-checkbox" : ""
        }`}
      >
        {!isCheckbox ? renderLabel() : null}
        <input {...inputUncontrolledProps} />
        {isCheckbox ? renderLabel() : null}
      </div>
    );
  }

  return (
    <Controller
      control={control}
      name={fieldName}
      render={({ field: { onChange, onBlur, value }, fieldState }) => {
        const invalid = fieldState.invalid;
        const inputControllerProps = {
          ...inputProps,
          value: value || "",
          onChange,
          onBlur,
          className: `${inputProps.className} ${
            invalid && !ignoreError ? "is-invalid" : ""
          } ${isCheckbox ? "custom-control-input" : ""}`,
        };
        if (isCheckbox) {
          inputControllerProps.checked = !!value;
        }

        return (
          <div
            className={`form-group ${inputProps.required ? "required" : ""} ${
              isCheckbox ? "custom-control custom-checkbox" : ""
            }`}
          >
            {!isCheckbox ? renderLabel() : null}
            <input {...inputControllerProps} />
            {isCheckbox ? renderLabel() : null}
            {renderError(fieldState)}
          </div>
        );
      }}
    />
  );
};
